// Ported from the desktop app (ui/src/theme/knobMath.ts).
// Pure knob math: value <-> normalized <-> angle mapping, log taper, drag deltas, snapping.
// No DOM here so it can be unit-tested in node.

/** Knob sweep: from -135deg (min) to +135deg (max), 0deg = straight up. */
export const KNOB_START_DEG = -135;
export const KNOB_END_DEG = 135;
export const KNOB_SWEEP_DEG = KNOB_END_DEG - KNOB_START_DEG;

/** Pixels of vertical drag for a full min->max sweep (normal and fine/shift). */
export const DRAG_PX_FULL = 200;
export const FINE_FACTOR = 10;

export function clamp(v: number, lo: number, hi: number): number {
  if (Number.isNaN(v)) return lo;
  return v < lo ? lo : v > hi ? hi : v;
}

/** True when a log taper is usable for this range (strictly positive bounds). */
export function canLog(min: number, max: number): boolean {
  return min > 0 && max > min;
}

/** Map a value to 0..1. With `log`, uses a logarithmic taper (needs min > 0). */
export function valueToNorm(v: number, min: number, max: number, log = false): number {
  if (max === min) return 0;
  const c = clamp(v, Math.min(min, max), Math.max(min, max));
  if (log && canLog(min, max)) return clamp(Math.log(c / min) / Math.log(max / min), 0, 1);
  return clamp((c - min) / (max - min), 0, 1);
}

/** Inverse of valueToNorm. */
export function normToValue(n: number, min: number, max: number, log = false): number {
  const t = clamp(n, 0, 1);
  if (log && canLog(min, max)) return min * Math.pow(max / min, t);
  return min + (max - min) * t;
}

/** 0..1 -> degrees (0 = up, clockwise positive). */
export function normToAngle(n: number): number {
  return KNOB_START_DEG + clamp(n, 0, 1) * KNOB_SWEEP_DEG;
}

/** Degrees -> 0..1 (clamped to the sweep). */
export function angleToNorm(deg: number): number {
  return clamp((deg - KNOB_START_DEG) / KNOB_SWEEP_DEG, 0, 1);
}

export function valueToAngle(v: number, min: number, max: number, log = false): number {
  return normToAngle(valueToNorm(v, min, max, log));
}

/** Snap to a step grid anchored at `min`, then clamp. step <= 0 disables snapping. */
export function snapToStep(v: number, step: number | undefined, min: number, max: number): number {
  let out = v;
  if (step !== undefined && step > 0) {
    out = min + Math.round((v - min) / step) * step;
    // Kill floating-point fuzz (e.g. 0.30000000000000004).
    const decimals = Math.max(0, Math.min(10, Math.ceil(-Math.log10(step)) + 1));
    out = Number(out.toFixed(decimals));
  }
  return clamp(out, Math.min(min, max), Math.max(min, max));
}

export interface DragOptions {
  min: number;
  max: number;
  log?: boolean;
  step?: number;
  fine?: boolean;
  /** Pixels for a full sweep; defaults to DRAG_PX_FULL. */
  pxFull?: number;
}

/**
 * Value after a vertical drag. `dy` is pointer movement in px (positive = down, as in screen
 * coords), so dragging up increases the value. Works in normalized space so log knobs feel even.
 */
export function dragToValue(startValue: number, dy: number, o: DragOptions): number {
  const px = (o.pxFull ?? DRAG_PX_FULL) * (o.fine ? FINE_FACTOR : 1);
  const n0 = valueToNorm(startValue, o.min, o.max, o.log);
  const n = clamp(n0 - dy / px, 0, 1);
  return snapToStep(normToValue(n, o.min, o.max, o.log), o.step, o.min, o.max);
}

/** Nudge by a number of "ticks" (wheel notches / arrow presses). 1 tick = step, or 1% of range. */
export function nudgeValue(value: number, ticks: number, o: DragOptions): number {
  if (o.step !== undefined && o.step > 0 && !o.log) {
    const s = o.fine ? o.step : o.step * Math.max(1, Math.round((o.max - o.min) / o.step / 100));
    return snapToStep(value + ticks * s, o.step, o.min, o.max);
  }
  const dn = (o.fine ? 0.001 : 0.01) * ticks;
  const n = clamp(valueToNorm(value, o.min, o.max, o.log) + dn, 0, 1);
  return snapToStep(normToValue(n, o.min, o.max, o.log), o.step, o.min, o.max);
}

/** Point on a circle; angle in degrees with 0 = up, clockwise. */
export function polar(cx: number, cy: number, r: number, deg: number): { x: number; y: number } {
  const a = ((deg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
}

/** SVG arc path from angle a0 to a1 (degrees, any order). Empty string for zero-length arcs. */
export function arcPath(cx: number, cy: number, r: number, a0: number, a1: number): string {
  const from = Math.min(a0, a1);
  const to = Math.max(a0, a1);
  if (to - from < 0.01) return "";
  const p0 = polar(cx, cy, r, from);
  const p1 = polar(cx, cy, r, to);
  const large = to - from > 180 ? 1 : 0;
  const f = (n: number) => n.toFixed(3);
  return `M ${f(p0.x)} ${f(p0.y)} A ${f(r)} ${f(r)} 0 ${large} 1 ${f(p1.x)} ${f(p1.y)}`;
}

/**
 * Angles for the value arc. Bipolar knobs draw from the center (value at the midpoint of the
 * range, or 0 if 0 lies inside the range) to the value; unipolar ones draw from the start.
 */
export function valueArcAngles(
  value: number,
  min: number,
  max: number,
  bipolar = false,
  log = false,
): [number, number] {
  const a = valueToAngle(value, min, max, log);
  if (!bipolar) return [KNOB_START_DEG, a];
  const center = min < 0 && max > 0 ? 0 : (min + max) / 2;
  return [valueToAngle(center, min, max, log), a];
}

/** Default value formatter: trims to a sensible precision for the range. */
export function formatKnobValue(v: number, min: number, max: number): string {
  const span = Math.abs(max - min);
  const digits = span >= 100 ? 0 : span >= 10 ? 1 : 2;
  const s = v.toFixed(digits);
  return s === "-0" || /^-0\.0+$/.test(s) ? s.slice(1) : s;
}
